import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Empty, Input, Skeleton, Tag, Tooltip, Typography } from 'antd';
import {
  CheckCircleOutlined,
  InboxOutlined,
  SearchOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  PZTransportCandidateDto,
  SupplierOrderPZTransportCandidateDto,
} from '../../../dto/pzTransportCandidates';
import { getSupplierOrderDisplayNumber } from '../../../dto/supplierOrder';
import {
  createSupplierOrderPZ,
  fetchPZTransportCandidates,
  type SupplierOrderPZGenerationOptions,
} from '../../orders/api/supplierOrdersApi';
import {
  formatSupplierOrderQuantityValue,
  formatSupplierOrderUnit,
} from '../../orders/supplierOrderUnits';
import { buildTransportRouteLabel } from '../../orders/transportAddressUtils';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';
import { getPZErrorMessage } from '../pzErrorMessages';
import { useFeedback } from '../../../utils/feedback';
import { formatProductDimensions } from '../../../utils/productDimensions';
import './PZTransportSettlementPanel.css';

interface PZTransportSettlementPanelProps {
  canCreate: boolean;
}

type SettlementState = 'WAITING' | 'PARTIAL' | 'COMPLETE' | 'BLOCKED';

const getSettlementState = (supplierOrder: SupplierOrderPZTransportCandidateDto): SettlementState => {
  const receipts = (supplierOrder.items || []).map((item) => item.pzReceipt).filter(Boolean);
  const blocked = receipts.some((receipt) => Boolean(receipt?.blockReason) && !receipt?.eligible);
  const received = receipts.some((receipt) => Number(receipt?.coveredQuantity || 0) > 0);
  const remaining = receipts.some((receipt) => Number(receipt?.remainingQuantity || 0) > 0);

  if (!remaining && receipts.length > 0) return 'COMPLETE';
  if (blocked && !receipts.some((receipt) => receipt?.eligible)) return 'BLOCKED';
  if (received) return 'PARTIAL';
  return 'WAITING';
};

const PZTransportSettlementPanel: React.FC<PZTransportSettlementPanelProps> = ({ canCreate }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['pz', 'common', 'orders']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [candidates, setCandidates] = useState<PZTransportCandidateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [generatingKey, setGeneratingKey] = useState<string | null>(null);

  const loadCandidates = useCallback(async (searchValue: string) => {
    setLoading(true);
    try {
      const trimmed = searchValue.trim();
      const response = await fetchPZTransportCandidates({
        includeCompleted: 'true',
        search: trimmed.length >= 3 ? trimmed : undefined,
      });
      setCandidates(response);
    } catch (error) {
      feedback.error(getPZErrorMessage(error, t, 'pz:transportSettlement.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [feedback, t]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void loadCandidates(search);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [loadCandidates, search]);

  const transportCountLabel = useMemo(() => t('pz:transportSettlement.transportCount', {
    count: candidates.length,
  }), [candidates.length, t]);

  const handleCreatePZ = async (
    transportId: string,
    supplierOrder: SupplierOrderPZTransportCandidateDto,
  ) => {
    const key = `${transportId}:${supplierOrder.id}`;
    setGeneratingKey(key);
    try {
      const result = await submitWithOperationalException(
        (payload) => createSupplierOrderPZ(
          supplierOrder.id,
          payload as SupplierOrderPZGenerationOptions,
        ),
        { sourceTransportIds: [transportId] },
        modal,
        t,
      );
      if (!result) return;

      feedback.success(t('pz:generateFromSupplierOrder.created', {
        pzNumber: result.pzNumber,
        count: result.createdItemCount,
      }));
      navigate(`/app/pz/${result.pzDocumentId}?mode=edit&supplierOrderId=${encodeURIComponent(supplierOrder.id)}`);
    } catch (error) {
      feedback.error(getPZErrorMessage(error, t, 'pz:generateFromSupplierOrder.createFailed'));
    } finally {
      setGeneratingKey(null);
    }
  };

  return (
    <section className="pz-transport-settlement" aria-labelledby="pz-transport-settlement-title">
      <div className="pz-transport-settlement__heading">
        <div>
          <Typography.Title id="pz-transport-settlement-title" level={3}>
            {t('pz:transportSettlement.title')}
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            {t('pz:transportSettlement.description')}
          </Typography.Paragraph>
        </div>
        <Tag className="pz-transport-settlement__count">{transportCountLabel}</Tag>
      </div>

      <Input
        className="pz-transport-settlement__search"
        allowClear
        prefix={<SearchOutlined />}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('pz:transportSettlement.searchPlaceholder')}
      />

      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : candidates.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('pz:transportSettlement.empty')}
        />
      ) : (
        <div className="pz-transport-settlement__list">
          {candidates.map((candidate) => {
            const { transport } = candidate;
            const route = buildTransportRouteLabel(
              transport.loadingAddresses,
              transport.deliveryAddresses,
              transport.originCity,
              transport.destinationCity,
            );
            const plate = transport.licensePlate1 || transport.vehicleRegistration;

            return (
              <article className="pz-transport-settlement__transport" key={transport.id}>
                <header className="pz-transport-settlement__transport-header">
                  <div className="pz-transport-settlement__transport-identity">
                    <TruckOutlined aria-hidden="true" />
                    <div>
                      <button
                        type="button"
                        className="pz-transport-settlement__transport-link"
                        onClick={() => navigate(`/app/transports/${transport.id}`)}
                      >
                        {transport.routeNumber}
                      </button>
                      <span>{[plate, transport.transportCompany, route.label].filter(Boolean).join(' · ')}</span>
                    </div>
                  </div>
                </header>

                <div className="pz-transport-settlement__orders">
                  {candidate.supplierOrders.map((supplierOrder) => {
                    const state = getSettlementState(supplierOrder);
                    const eligible = (supplierOrder.items || []).some((item) => item.pzReceipt?.eligible);
                    const blockedReason = (supplierOrder.items || [])
                      .map((item) => item.pzReceipt?.blockReason)
                      .find(Boolean);
                    const actionKey = `${transport.id}:${supplierOrder.id}`;

                    return (
                      <div className="pz-transport-settlement__order" key={supplierOrder.id}>
                        <div className="pz-transport-settlement__order-heading">
                          <div>
                            <button
                              type="button"
                              className="pz-transport-settlement__zd-link"
                              onClick={() => navigate(`/app/supplier-orders/${supplierOrder.id}`)}
                            >
                              {getSupplierOrderDisplayNumber(supplierOrder)}
                            </button>
                            <span>{supplierOrder.supplier?.name || t('pz:transportSettlement.supplierFallback')}</span>
                          </div>
                          <Tag color={state === 'COMPLETE' ? 'success' : state === 'PARTIAL' ? 'processing' : state === 'BLOCKED' ? 'warning' : 'default'}>
                            {state === 'COMPLETE' ? <CheckCircleOutlined /> : <InboxOutlined />}
                            {' '}{t(`pz:transportSettlement.status.${state.toLowerCase()}`)}
                          </Tag>
                        </div>

                        <div className="pz-transport-settlement__column-labels" aria-hidden="true">
                          <span>{t('pz:transportSettlement.product')}</span>
                          <span>{t('pz:transportSettlement.onTransport')}</span>
                          <span>{t('pz:transportSettlement.received')}</span>
                          <span>{t('pz:transportSettlement.remaining')}</span>
                        </div>

                        <div className="pz-transport-settlement__items">
                          {(supplierOrder.items || []).map((item) => {
                            const receipt = item.pzReceipt;
                            const unit = formatSupplierOrderUnit(item.unit || 'pcs', t, i18n.language);
                            const dimensions = formatProductDimensions(item);
                            return (
                              <div className="pz-transport-settlement__item" key={item.id}>
                                <div className="pz-transport-settlement__product">
                                  <strong>{item.name}</strong>
                                  {dimensions ? <span>{dimensions}</span> : null}
                                  {receipt?.documents?.length ? (
                                    <div className="pz-transport-settlement__documents">
                                      {receipt.documents.map((document) => (
                                        <Button
                                          key={document.pzDocumentId}
                                          type="link"
                                          size="small"
                                          onClick={() => navigate(`/app/pz/${document.pzDocumentId}`)}
                                        >
                                          {document.pzNumber}: {formatSupplierOrderQuantityValue(document.quantity, i18n.language)} {unit}
                                        </Button>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                                <span data-label={t('pz:transportSettlement.onTransport')}>
                                  {formatSupplierOrderQuantityValue(receipt?.quantity || 0, i18n.language)} {unit}
                                </span>
                                <span data-label={t('pz:transportSettlement.received')} className="pz-transport-settlement__received">
                                  {formatSupplierOrderQuantityValue(receipt?.coveredQuantity || 0, i18n.language)} {unit}
                                </span>
                                <strong data-label={t('pz:transportSettlement.remaining')} className={Number(receipt?.remainingQuantity || 0) > 0 ? 'pz-transport-settlement__remaining' : ''}>
                                  {formatSupplierOrderQuantityValue(receipt?.remainingQuantity || 0, i18n.language)} {unit}
                                </strong>
                              </div>
                            );
                          })}
                        </div>

                        <div className="pz-transport-settlement__order-action">
                          {blockedReason && !eligible && state !== 'COMPLETE' ? (
                            <Typography.Text type="warning">{blockedReason}</Typography.Text>
                          ) : (
                            <Typography.Text type="secondary">
                              {eligible
                                ? t('pz:transportSettlement.actionHint')
                                : t('pz:transportSettlement.completeHint')}
                            </Typography.Text>
                          )}
                          {canCreate ? (
                            <Tooltip title={!eligible ? (blockedReason || t('pz:transportSettlement.completeHint')) : ''}>
                              <span>
                                <Button
                                  type={eligible ? 'primary' : 'default'}
                                  disabled={!eligible}
                                  loading={generatingKey === actionKey}
                                  onClick={() => void handleCreatePZ(transport.id, supplierOrder)}
                                >
                                  {t('pz:transportSettlement.createRemaining')}
                                </Button>
                              </span>
                            </Tooltip>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default PZTransportSettlementPanel;
